import React, { useState, useEffect } from 'react';
import {
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Box,
    Modal,
    Button,
    IconButton,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    CircularProgress,
    Alert
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from 'react-redux';
import { obtenerInventarioPorAnexoAccion, limpiarInventario } from '../../../../redux/DucksAtencionACliente/InventarioInstalacionDucks';

function ModalInventarioInstalacionComponent({ open, handleClose, dataInstalacion }) {
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('TODOS');

    const { inventario: inventarioDataState, loading, error } = useSelector(state => state.inventarioInstalacion || {});

    const inventarioData = inventarioDataState || {
        todos: [],
        porTipo: {
            granel: [],
            barrica: [],
            vidrio: []
        },
        resumen: {
            totalLotes: 0,
            totalGranelLitros: 0,
            totalBarricasCount: 0,
            totalBarricaLitros: 0,
            totalVidrioLitros: 0
        }
    };

    const infoGeneral = dataInstalacion?.informaciongeneralinstalacioncliente || {};
    const tipoAnexo = dataInstalacion?.tipoanexo?.descripcion || 'INSTALACIÓN';
    const alias = infoGeneral.alias || 'S/N';
    const idAnexoCliente = dataInstalacion?.idanexocliente;

    const domicilioCompleto = [
        infoGeneral.calle,
        infoGeneral.numerointerior ? `Int. ${infoGeneral.numerointerior}` : null,
        infoGeneral.numeroexterior ? `Ext. ${infoGeneral.numeroexterior}` : null,
        infoGeneral.colonia ? `Col. ${infoGeneral.colonia}` : null,
        infoGeneral.localidad,
        infoGeneral.municipio,
        infoGeneral.estado,
        infoGeneral.codigopostal ? `C.P. ${infoGeneral.codigopostal}` : null
    ].filter(Boolean).join(', ');

    useEffect(() => {
        if (open && idAnexoCliente) {
            dispatch(obtenerInventarioPorAnexoAccion(idAnexoCliente));
        } else if (!open) {
            dispatch(limpiarInventario());
        }
    }, [open, idAnexoCliente, dispatch]);


    // Determine raw list based on tab
    let listadoBase = inventarioData.todos || [];
    if (activeTab === 'GRANEL') {
        listadoBase = inventarioData.porTipo?.granel || [];
    } else if (activeTab === 'BARRICA') {
        listadoBase = inventarioData.porTipo?.barrica || [];
    } else if (activeTab === 'VIDRIO') {
        listadoBase = inventarioData.porTipo?.vidrio || [];
    }

    // Apply search query
    const filteredLotes = listadoBase.filter(item => {
        const query = searchTerm.toLowerCase();
        return (
            (item.no_lote || '').toLowerCase().includes(query) ||
            (item.no_tanque || '').toLowerCase().includes(query) ||
            (item.no_barricas || '').toString().toLowerCase().includes(query) ||
            (item.folio || '').toLowerCase().includes(query) ||
            (item.especie || '').toLowerCase().includes(query) ||
            (item.categoria || '').toLowerCase().includes(query) ||
            (item.clase || '').toLowerCase().includes(query) ||
            (item.clave_fq || '').toLowerCase().includes(query) ||
            (item.tipo_inventario || '').toLowerCase().includes(query) ||
            (item.ingrediente || '').toLowerCase().includes(query)
        );
    });

    const resumen = inventarioData.resumen || {};

    const styleModal = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '92%',
        maxWidth: 1200,
        maxHeight: '92vh',
        bgcolor: '#ffffff',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        borderRadius: '16px',
        outline: 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    };

    const theme = createTheme({
        palette: {
            primary: {
                main: '#8ABE3E'
            },
            secondary: {
                main: '#DC3545'
            }
        },
        typography: {
            fontFamily: 'Montserrat, sans-serif'
        }
    });

    const getTipoChipStyle = (tipoStr) => {
        const t = (tipoStr || '').toLowerCase();
        if (t.includes('barrica')) {
            return { bg: '#FEF3C7', color: '#B45309', label: tipoStr || 'Barrica' };
        } else if (t.includes('vidrio') || t.includes('botella') || t.includes('terminado')) {
            return { bg: '#D1FAE5', color: '#047857', label: tipoStr || 'Vidrio' };
        }
        return { bg: '#E0F2FE', color: '#0369A1', label: tipoStr || 'Granel' };
    };

    const renderTableHeader = () => {
        if (activeTab === 'GRANEL') {
            return (
                <TableRow>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>FECHA</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>NO. DE TANQUE</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>NO. DE LOTE</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>ESPECIE</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>CATEGORÍA</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>CLASE</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>CLAVE FQ</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>INGREDIENTE</TableCell>
                    <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 700 }}>LITROS</TableCell>
                    <TableCell align="center" sx={{ color: '#ffffff', fontWeight: 700 }}>% ALC. VOL.</TableCell>
                </TableRow>
            );
        }

        if (activeTab === 'BARRICA' || activeTab === 'VIDRIO') {
            return (
                <TableRow>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>FECHA</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>NO. DE LOTE</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>NO. DE BARRICAS</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>FOLIO</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>ESPECIE</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>CATEGORÍA</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>CLASE</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>CLAVE FQ</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>INGREDIENTE</TableCell>
                    <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 700 }}>LITROS</TableCell>
                    <TableCell align="center" sx={{ color: '#ffffff', fontWeight: 700 }}>% ALC. VOL.</TableCell>
                </TableRow>
            );
        }

        // Default: TODOS tab
        return (
            <TableRow>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>TIPO</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>FECHA</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>NO. TANQUE / LOTE</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>BARRICAS / FOLIO</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>ESPECIE</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>CATEGORÍA</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>CLASE</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>CLAVE FQ</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>INGREDIENTE</TableCell>
                <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 700 }}>LITROS</TableCell>
                <TableCell align="center" sx={{ color: '#ffffff', fontWeight: 700 }}>% ALC. VOL.</TableCell>
            </TableRow>
        );
    };

    const renderTableRow = (item) => {
        const fechaFormateada = item.fecha ? item.fecha.toString().substring(0, 10) : '---';
        const litrosFormateado = item.litros !== undefined && item.litros !== null && item.litros !== '' ? Number(item.litros).toLocaleString() : '---';
        const alcVolFormateado = item.porcentaje_alc_vol !== undefined && item.porcentaje_alc_vol !== null && item.porcentaje_alc_vol !== '' ? `${item.porcentaje_alc_vol}%` : '---';

        if (activeTab === 'GRANEL') {
            return (
                <TableRow
                    key={item.idinventario}
                    sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#F8FAFC' },
                        '&:hover': { backgroundColor: '#F1F5F9' },
                        transition: 'background-color 0.2s'
                    }}
                >
                    <TableCell sx={{ color: '#64748B', fontSize: '13px' }}>{fechaFormateada}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#0F172A' }}>{item.no_tanque || '---'}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#0F172A' }}>{item.no_lote || '---'}</TableCell>
                    <TableCell sx={{ color: '#334155' }}>{item.especie || '---'}</TableCell>
                    <TableCell sx={{ color: '#475569' }}>{item.categoria || '---'}</TableCell>
                    <TableCell sx={{ color: '#475569' }}>{item.clase || '---'}</TableCell>
                    <TableCell sx={{ color: '#475569', fontWeight: 500 }}>{item.clave_fq || '---'}</TableCell>
                    <TableCell sx={{ color: '#475569' }}>{item.ingrediente || '---'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {litrosFormateado !== '---' ? `${litrosFormateado} L` : '---'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#475569' }}>{alcVolFormateado}</TableCell>
                </TableRow>
            );
        }

        if (activeTab === 'BARRICA' || activeTab === 'VIDRIO') {
            return (
                <TableRow
                    key={item.idinventario}
                    sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#F8FAFC' },
                        '&:hover': { backgroundColor: '#F1F5F9' },
                        transition: 'background-color 0.2s'
                    }}
                >
                    <TableCell sx={{ color: '#64748B', fontSize: '13px' }}>{fechaFormateada}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#0F172A' }}>{item.no_lote || '---'}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#B45309' }}>{item.no_barricas || '---'}</TableCell>
                    <TableCell sx={{ color: '#475569', fontWeight: 500 }}>{item.folio || '---'}</TableCell>
                    <TableCell sx={{ color: '#334155' }}>{item.especie || '---'}</TableCell>
                    <TableCell sx={{ color: '#475569' }}>{item.categoria || '---'}</TableCell>
                    <TableCell sx={{ color: '#475569' }}>{item.clase || '---'}</TableCell>
                    <TableCell sx={{ color: '#475569', fontWeight: 500 }}>{item.clave_fq || '---'}</TableCell>
                    <TableCell sx={{ color: '#475569' }}>{item.ingrediente || '---'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {litrosFormateado !== '---' ? `${litrosFormateado} L` : '---'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#475569' }}>{alcVolFormateado}</TableCell>
                </TableRow>
            );
        }

        // Default: TODOS tab
        const chipStyle = getTipoChipStyle(item.tipo_inventario);
        return (
            <TableRow
                key={item.idinventario}
                sx={{
                    '&:nth-of-type(odd)': { backgroundColor: '#F8FAFC' },
                    '&:hover': { backgroundColor: '#F1F5F9' },
                    transition: 'background-color 0.2s'
                }}
            >
                <TableCell sx={{ fontWeight: 600 }}>
                    <Chip label={chipStyle.label} size="small" sx={{ backgroundColor: chipStyle.bg, color: chipStyle.color, fontWeight: 600 }} />
                </TableCell>
                <TableCell sx={{ color: '#64748B', fontSize: '13px' }}>{fechaFormateada}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0F172A' }}>{item.no_lote || item.no_tanque || '---'}</TableCell>
                <TableCell sx={{ color: '#475569' }}>{item.no_barricas ? `${item.no_barricas} Barr.` : (item.folio || '---')}</TableCell>
                <TableCell sx={{ color: '#334155' }}>{item.especie || '---'}</TableCell>
                <TableCell sx={{ color: '#475569' }}>{item.categoria || '---'}</TableCell>
                <TableCell sx={{ color: '#475569' }}>{item.clase || '---'}</TableCell>
                <TableCell sx={{ color: '#475569', fontWeight: 500 }}>{item.clave_fq || '---'}</TableCell>
                <TableCell sx={{ color: '#475569' }}>{item.ingrediente || '---'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#0F172A' }}>
                    {litrosFormateado !== '---' ? `${litrosFormateado} L` : '---'}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#475569' }}>{alcVolFormateado}</TableCell>
            </TableRow>
        );
    };

    return (
        <ThemeProvider theme={theme}>
            <Modal open={open} onClose={handleClose} aria-labelledby="modal-inventario-title">
                <Box sx={styleModal}>
                    {/* Header */}
                    <Box sx={{
                        background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                        color: '#ffffff',
                        px: 3,
                        py: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '3px solid #EDB75A'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                                backgroundColor: 'rgba(237, 183, 90, 0.2)',
                                p: 1,
                                borderRadius: '12px',
                                display: 'flex'
                            }}>
                                <InventoryIcon sx={{ color: '#EDB75A', fontSize: 32 }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
                                    INVENTARIO DE INSTALACIÓN - {tipoAnexo}
                                </Typography>
                                <Typography id="modal-inventario-title" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#ffffff' }}>
                                    {alias.toUpperCase()}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={handleClose} sx={{ color: '#94A3B8', '&:hover': { color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Content Body */}
                    <Box sx={{ p: 3, overflowY: 'auto', flex: 1, backgroundColor: '#F8FAFC' }}>
                        {/* Ubicación Bar */}
                        <Paper elevation={0} sx={{ p: 1.5, px: 2, mb: 2.5, borderRadius: '10px', border: '1px solid #E2E8F0', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOnIcon sx={{ color: '#8ABE3E', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>
                                <b>Ubicación:</b> {domicilioCompleto || 'Sin dirección asignada'}
                            </Typography>
                        </Paper>

                        {/* Summary KPIs */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
                            <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid #E2E8F0', backgroundColor: '#ffffff', borderLeft: '4px solid #8ABE3E' }}>
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>TOTAL REGISTROS</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', mt: 0.5 }}>
                                    {resumen.totalLotes || (inventarioData.todos ? inventarioData.todos.length : 0)}
                                </Typography>
                            </Paper>
                            <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid #E2E8F0', backgroundColor: '#ffffff', borderLeft: '4px solid #3B82F6' }}>
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>VOLUMEN GRANEL</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', mt: 0.5 }}>
                                    {(resumen.totalGranelLitros || 0).toLocaleString()} L
                                </Typography>
                            </Paper>
                            <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid #E2E8F0', backgroundColor: '#ffffff', borderLeft: '4px solid #F59E0B' }}>
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>BARRICAS / MADURACIÓN</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', mt: 0.5 }}>
                                    {resumen.totalBarricasCount || (inventarioData.porTipo?.barrica ? inventarioData.porTipo.barrica.length : 0)} Und.
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748B', fontSize: '11px' }}>
                                    {(resumen.totalBarricaLitros || 0).toLocaleString()} Litros
                                </Typography>
                            </Paper>
                            <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid #E2E8F0', backgroundColor: '#ffffff', borderLeft: '4px solid #10B981' }}>
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>PRODUCTO EN VIDRIO</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', mt: 0.5 }}>
                                    {(resumen.totalVidrioLitros || 0).toLocaleString()} L
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748B', fontSize: '11px' }}>
                                    {(inventarioData.porTipo?.vidrio ? inventarioData.porTipo.vidrio.length : 0)} Lotes
                                </Typography>
                            </Paper>
                        </Box>

                        {/* Error Alert */}
                        {error && (
                            <Alert severity="warning" sx={{ mb: 2.5, borderRadius: '10px' }}>
                                {error}
                            </Alert>
                        )}

                        {/* Search and Tabs Bar */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
                            <Tabs
                                value={activeTab}
                                onChange={(e, val) => setActiveTab(val)}
                                sx={{
                                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '14px', minHeight: 40 },
                                    '& .Mui-selected': { color: '#8ABE3E' },
                                    '& .MuiTabs-indicator': { backgroundColor: '#8ABE3E' }
                                }}
                            >
                                <Tab label={`Todos (${inventarioData.todos?.length || 0})`} value="TODOS" />
                                <Tab label={`Granel (${inventarioData.porTipo?.granel?.length || 0})`} value="GRANEL" />
                                <Tab label={`Barrica (${inventarioData.porTipo?.barrica?.length || 0})`} value="BARRICA" />
                                <Tab label={`Vidrio (${inventarioData.porTipo?.vidrio?.length || 0})`} value="VIDRIO" />
                            </Tabs>

                            <TextField
                                size="small"
                                placeholder="Buscar por no. lote, tanque, folio, maguey..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: '#94A3B8' }} />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ minWidth: 280, backgroundColor: '#ffffff', borderRadius: '8px' }}
                            />
                        </Box>

                        {/* Loading / Table / Empty States */}
                        {loading ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                                <CircularProgress sx={{ color: '#8ABE3E', mb: 2 }} />
                                <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500 }}>
                                    Cargando inventario de la instalación...
                                </Typography>
                            </Box>
                        ) : filteredLotes.length === 0 ? (
                            <Paper elevation={0} sx={{ p: 5, textAlign: 'center', borderRadius: '12px', border: '1px dashed #CBD5E1', backgroundColor: '#ffffff' }}>
                                <InventoryIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
                                <Typography variant="h6" sx={{ color: '#475569', fontWeight: 600 }}>
                                    No hay registros de inventario para mostrar
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#94A3B8', mt: 0.5 }}>
                                    {searchTerm
                                        ? 'No se encontraron coincidencias para la búsqueda ingresada.'
                                        : 'Esta instalación aún no cuenta con registros cargados en esta categoría.'}
                                </Typography>
                            </Paper>
                        ) : (
                            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                                <Table sx={{ minWidth: 900 }}>
                                    <TableHead sx={{ backgroundColor: '#1E293B' }}>
                                        {renderTableHeader()}
                                    </TableHead>
                                    <TableBody>
                                        {filteredLotes.map(item => renderTableRow(item))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>

                    {/* Footer */}
                    <Box sx={{ p: 2, px: 3, borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end', backgroundColor: '#ffffff' }}>
                        <Button variant="contained" color="secondary" onClick={handleClose} sx={{ px: 4, py: 1, borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
                            Cerrar
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </ThemeProvider>
    );
}

export default ModalInventarioInstalacionComponent;
